'use client';

import React, { useState, useCallback } from 'react';
import { createClient } from '@/lib/supabase/client';

// ─── EMBED CODE GENERATOR ─────────────────────────────────────────────────────
// Generates standalone HTML embed snippets for any platform component.
// Can be planted on external websites and applications.

interface EmbedTarget {
  id: string;
  type: string;
  title: string;
  description: string;
  icon: string;
  embedPath: string;
}

const EMBED_TARGETS: EmbedTarget[] = [
  { id: 'irl-lens', type: 'irl_lens', title: 'IRL Lens', description: 'Inner Reality Lens — full 8-layer recognition tool', icon: '🔬', embedPath: '/embed/irl-lens' },
  { id: 'mrg', type: 'mrg', title: 'Muslim Resource Guide', description: 'Community resource map widget', icon: '🗺', embedPath: '/embed/mrg' },
  { id: 'names-tile', type: 'names_tile', title: 'Names Tile', description: 'Single Name of Allah with root analysis', icon: '✦', embedPath: '/embed/names' },
  { id: 'keys-card', type: 'keys_card', title: 'Keys Card', description: 'A single Key from the curriculum', icon: '🗝', embedPath: '/embed/keys' },
  { id: 'letter-section', type: 'letter_section', title: 'Letter Section', description: 'Any section of the Open Letter', icon: '📜', embedPath: '/embed/letter' },
  { id: 'sl-glossary-term', type: 'concept_term', title: 'Glossary Term', description: 'A single SL Glossary entry with root field', icon: '📖', embedPath: '/embed/glossary' },
];

interface GeneratedEmbed {
  token: string;
  iframeCode: string;
  scriptCode: string;
  linkCode: string;
}

interface EmbedCodeGeneratorProps {
  sectionId?: string;
  sectionTitle?: string;
  compact?: boolean;
}

export default function EmbedCodeGenerator({ sectionId, sectionTitle, compact = false }: EmbedCodeGeneratorProps) {
  const [open, setOpen] = useState(false);
  const [selected, setSelected] = useState<EmbedTarget | null>(
    sectionId ? { id: sectionId, type: 'letter_section', title: sectionTitle || sectionId, description: 'Open Letter section', icon: '📜', embedPath: `/embed/letter?section=${sectionId}` } : null
  );
  const [generated, setGenerated] = useState<GeneratedEmbed | null>(null);
  const [generating, setGenerating] = useState(false);
  const [copied, setCopied] = useState<string | null>(null);
  const [width, setWidth] = useState('100%');
  const [height, setHeight] = useState('600');
  const [theme, setTheme] = useState<'dark' | 'light'>('dark');

  const baseUrl = typeof window !== 'undefined' ? window.location.origin : 'https://haalproject.org';

  const generateEmbed = useCallback(async () => {
    if (!selected) return;
    setGenerating(true);
    const supabase = createClient();
    const { data: { user } } = await supabase.auth.getUser();

    let token = `haal-${selected.type}-${Date.now().toString(36)}`;

    if (user) {
      const { data } = await supabase
        .from('embed_codes')
        .insert({
          user_id: user.id,
          module_type: selected.type as any,
          module_ref: selected.id,
          title: selected.title,
        })
        .select()
        .single();
      if (data?.embed_token) token = data.embed_token;
    }

    const embedUrl = `${baseUrl}${selected.embedPath}?token=${token}&theme=${theme}`;
    const iframeCode = `<iframe
  src="${embedUrl}"
  width="${width}"
  height="${height}"
  frameborder="0"
  style="border-radius:8px;overflow:hidden;"
  title="${selected.title} — Haal Project"
  loading="lazy"
></iframe>`;

    const scriptCode = `<div id="haal-widget-${token}"></div>
<script>
  (function(){
    var w=document.getElementById('haal-widget-${token}');
    var f=document.createElement('iframe');
    f.src='${embedUrl}';
    f.width='${width}';f.height='${height}';
    f.frameBorder='0';f.style.borderRadius='8px';
    f.title='${selected.title} — Haal Project';
    w.appendChild(f);
  })();
</script>`;

    const linkCode = `${embedUrl}`;

    setGenerated({ token, iframeCode, scriptCode, linkCode });
    setGenerating(false);
  }, [selected, width, height, theme, baseUrl]);

  const copyToClipboard = useCallback((text: string, key: string) => {
    navigator.clipboard.writeText(text).then(() => {
      setCopied(key);
      setTimeout(() => setCopied(null), 2000);
    });
  }, []);

  if (compact) {
    return (
      <div style={{ marginTop: 16 }}>
        <button
          onClick={() => setOpen(!open)}
          style={{
            display: 'flex', alignItems: 'center', gap: 8, background: 'none',
            border: '1px solid rgba(45,138,135,0.2)', borderRadius: 4,
            padding: '5px 12px', cursor: 'pointer', color: 'rgba(45,138,135,0.7)',
          }}
        >
          <span style={{ fontFamily: 'monospace', fontSize: 9, letterSpacing: '.1em', textTransform: 'uppercase' }}>
            {'</>'} Embed / Share
          </span>
        </button>
        {open && (
          <div style={{ marginTop: 10, padding: '14px', background: 'rgba(6,13,18,0.95)', border: '1px solid rgba(45,138,135,0.15)', borderRadius: 8 }}>
            <EmbedCodeInner
              selected={selected}
              generated={generated}
              generating={generating}
              copied={copied}
              width={width}
              height={height}
              theme={theme}
              setWidth={setWidth}
              setHeight={setHeight}
              setTheme={setTheme}
              generateEmbed={generateEmbed}
              copyToClipboard={copyToClipboard}
              targets={EMBED_TARGETS}
              setSelected={setSelected}
              compact
            />
          </div>
        )}
      </div>
    );
  }

  return (
    <div style={{ background: 'rgba(6,13,18,0.98)', borderTop: '1px solid rgba(45,138,135,0.1)', padding: '40px 0' }}>
      <div className="max-w-[900px] mx-auto px-6 md:px-10">
        <button
          onClick={() => setOpen(!open)}
          style={{
            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            width: '100%', background: 'rgba(45,138,135,0.04)',
            border: '1px solid rgba(45,138,135,0.15)', borderRadius: 8,
            padding: '14px 20px', cursor: 'pointer',
          }}
          aria-expanded={open}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <span style={{ fontFamily: 'monospace', fontSize: 9, letterSpacing: '.14em', textTransform: 'uppercase', color: 'rgba(45,138,135,0.8)' }}>
              {'</>'} Embed & Share Toolkit
            </span>
            <span style={{ fontFamily: 'monospace', fontSize: 9, color: 'rgba(212,197,169,0.3)' }}>
              plant any module on external sites · standalone HTML widgets
            </span>
          </div>
          <span style={{ color: 'rgba(45,138,135,0.5)', fontSize: 11, transform: open ? 'rotate(180deg)' : 'none', transition: 'transform 0.2s' }}>▼</span>
        </button>

        {open && (
          <div style={{ marginTop: 16, animation: 'ecFadeIn 0.22s ease' }}>
            <style>{`@keyframes ecFadeIn { from { opacity: 0; transform: translateY(-4px); } to { opacity: 1; transform: translateY(0); } }`}</style>
            <EmbedCodeInner
              selected={selected}
              generated={generated}
              generating={generating}
              copied={copied}
              width={width}
              height={height}
              theme={theme}
              setWidth={setWidth}
              setHeight={setHeight}
              setTheme={setTheme}
              generateEmbed={generateEmbed}
              copyToClipboard={copyToClipboard}
              targets={EMBED_TARGETS}
              setSelected={setSelected}
              compact={false}
            />
          </div>
        )}
      </div>
    </div>
  );
}

interface EmbedCodeInnerProps {
  selected: EmbedTarget | null;
  generated: GeneratedEmbed | null;
  generating: boolean;
  copied: string | null;
  width: string;
  height: string;
  theme: 'dark' | 'light';
  setWidth: (v: string) => void;
  setHeight: (v: string) => void;
  setTheme: (v: 'dark' | 'light') => void;
  generateEmbed: () => void;
  copyToClipboard: (text: string, key: string) => void;
  targets: EmbedTarget[];
  setSelected: (t: EmbedTarget | null) => void;
  compact: boolean;
}

function EmbedCodeInner({ selected, generated, generating, copied, width, height, theme, setWidth, setHeight, setTheme, generateEmbed, copyToClipboard, targets, setSelected, compact }: EmbedCodeInnerProps) {
  return (
    <div>
      {/* Module selector */}
      {!compact && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: 8, marginBottom: 16 }}>
          {targets.map(t => (
            <button
              key={t.id}
              onClick={() => setSelected(t)}
              style={{
                display: 'flex', alignItems: 'center', gap: 8, padding: '10px 12px',
                background: selected?.id === t.id ? 'rgba(45,138,135,0.1)' : 'rgba(255,255,255,0.02)',
                border: `1px solid ${selected?.id === t.id ? 'rgba(45,138,135,0.4)' : 'rgba(255,255,255,0.06)'}`,
                borderRadius: 6, cursor: 'pointer', textAlign: 'left',
              }}
            >
              <span style={{ fontSize: 14 }}>{t.icon}</span>
              <div>
                <div style={{ fontFamily: 'monospace', fontSize: 10, color: 'rgba(212,197,169,0.8)' }}>{t.title}</div>
                <div style={{ fontFamily: 'monospace', fontSize: 9, color: 'rgba(212,197,169,0.35)' }}>{t.description}</div>
              </div>
            </button>
          ))}
        </div>
      )}

      {/* Options */}
      <div style={{ display: 'flex', gap: 10, marginBottom: 14, flexWrap: 'wrap', alignItems: 'center' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <span style={{ fontFamily: 'monospace', fontSize: 9, color: 'rgba(212,197,169,0.4)' }}>W:</span>
          <input value={width} onChange={e => setWidth(e.target.value)} style={{ width: 70, background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.07)', borderRadius: 4, padding: '4px 8px', color: 'rgba(212,197,169,0.7)', fontFamily: 'monospace', fontSize: 10, outline: 'none' }} />
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <span style={{ fontFamily: 'monospace', fontSize: 9, color: 'rgba(212,197,169,0.4)' }}>H:</span>
          <input value={height} onChange={e => setHeight(e.target.value)} style={{ width: 60, background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.07)', borderRadius: 4, padding: '4px 8px', color: 'rgba(212,197,169,0.7)', fontFamily: 'monospace', fontSize: 10, outline: 'none' }} />
        </div>
        <div style={{ display: 'flex', gap: 4 }}>
          {(['dark', 'light'] as const).map(t => (
            <button key={t} onClick={() => setTheme(t)} style={{
              fontFamily: 'monospace', fontSize: 9, padding: '4px 10px',
              border: `1px solid ${theme === t ? 'rgba(45,138,135,0.4)' : 'rgba(255,255,255,0.07)'}`,
              background: theme === t ? 'rgba(45,138,135,0.08)' : 'transparent',
              color: theme === t ? 'rgba(45,138,135,0.9)' : 'rgba(212,197,169,0.4)',
              borderRadius: 4, cursor: 'pointer',
            }}>{t}</button>
          ))}
        </div>
        <button
          onClick={generateEmbed}
          disabled={!selected || generating}
          style={{
            fontFamily: 'monospace', fontSize: 9, letterSpacing: '.1em', textTransform: 'uppercase',
            padding: '5px 14px', border: '1px solid rgba(45,138,135,0.3)',
            background: 'rgba(45,138,135,0.08)', color: 'rgba(45,138,135,0.9)',
            borderRadius: 4, cursor: !selected || generating ? 'not-allowed' : 'pointer',
            opacity: !selected ? 0.4 : 1,
          }}
        >
          {generating ? '…generating' : '⚙ Generate Embed'}
        </button>
      </div>

      {/* Generated codes */}
      {generated && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          {[
            { key: 'iframe', label: 'iframe embed', code: generated.iframeCode },
            { key: 'script', label: 'script embed', code: generated.scriptCode },
            { key: 'link', label: 'direct link', code: generated.linkCode },
          ].map(({ key, label, code }) => (
            <div key={key} style={{ background: 'rgba(0,0,0,0.3)', border: '1px solid rgba(255,255,255,0.06)', borderRadius: 6, overflow: 'hidden' }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '6px 12px', borderBottom: '1px solid rgba(255,255,255,0.04)' }}>
                <span style={{ fontFamily: 'monospace', fontSize: 9, color: 'rgba(45,138,135,0.7)', letterSpacing: '.1em', textTransform: 'uppercase' }}>{label}</span>
                <button
                  onClick={() => copyToClipboard(code, key)}
                  style={{ fontFamily: 'monospace', fontSize: 9, background: 'none', border: 'none', color: copied === key ? 'rgba(45,200,100,0.8)' : 'rgba(212,197,169,0.4)', cursor: 'pointer' }}
                >
                  {copied === key ? '✓ Copied' : 'Copy'}
                </button>
              </div>
              <pre style={{ margin: 0, padding: '10px 12px', fontFamily: 'monospace', fontSize: 10, color: 'rgba(212,197,169,0.6)', overflowX: 'auto', whiteSpace: 'pre-wrap', wordBreak: 'break-all' }}>
                {code}
              </pre>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
